/**
 * CHAT HISTORY COMPONENTS
 * Monthly and weekly organization with expandable UI
 */

import React, { useState } from 'react';
import { CalendarDays, MessageCircle, Target, Dumbbell, PartyPopper, ChevronUp, ChevronDown } from 'lucide-react';
import { MonthGroup, WeekGroup, ChatHistory as ChatHistoryType } from '../../types/ChatHistory';
import './ChatHistory.css';

interface ChatHistoryProps {
  history: ChatHistoryType;
  onWeekExpand: (weekId: string) => void;
  onMonthToggle: (monthId: string) => void;
}

interface MonthHeaderProps {
  month: MonthGroup;
  onToggle: (monthId: string) => void;
}

interface WeekCardProps {
  week: WeekGroup;
  onExpand: (weekId: string) => void;
  showDetailedCard?: boolean;
}

interface DetailedWeekCardProps {
  week: WeekGroup;
  onLoadMessages: (weekId: string) => void;
}

// Week Card variants (Phase 6 — same model as NativeChatHistory): the
// UI honestly communicates state per week instead of fabricating a
// summary or rendering empty quotes.
type WeekCardVariant = 'processed' | 'processing' | 'unprocessed';

const computeWeekVariant = (week: WeekGroup, historyIsProcessing: boolean): WeekCardVariant => {
  const hasSummary = !!(week.summary && week.summary.trim().length > 0);
  if (hasSummary) return 'processed';
  return historyIsProcessing ? 'processing' : 'unprocessed';
};

// Week Card Component
const WeekCard: React.FC<WeekCardProps & { variant: WeekCardVariant }> = ({
  week,
  onExpand,
  showDetailedCard = false,
  variant,
}) => {
  if (variant === 'processing') {
    return (
      <div className="week-card week-card-skeleton" aria-label={`Week of ${week.dateRange}: Cedar is summarizing this week`}>
        <div className="week-summary">
          <div className="week-emoji-skeleton" aria-hidden="true" />
          <div className="week-content">
            <div className="week-date">{week.dateRange}</div>
            <div className="week-skeleton-text">
              <span className="week-skeleton-spinner" aria-hidden="true" />
              Cedar is summarizing this week…
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (variant === 'unprocessed') {
    const msgCount = week.stats?.messageCount ?? 0;
    return (
      <div className="week-card week-card-unprocessed">
        <div
          className="week-summary"
          onClick={() => onExpand(week.id)}
          role="button"
          tabIndex={0}
          aria-label={`Week of ${week.dateRange}: ${msgCount} messages — tap to read`}
        >
          <div className="week-emoji">📅</div>
          <div className="week-content">
            <div className="week-date">{week.dateRange}</div>
            <div className="week-unprocessed-hint">
              {msgCount > 0
                ? `${msgCount} message${msgCount === 1 ? '' : 's'} — tap to read`
                : 'Tap to open this week'}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="week-card">
      <div
        className="week-summary"
        onClick={() => onExpand(week.id)}
      >
        <div className="week-emoji">{week.emoji}</div>
        <div className="week-content">
          <div className="week-date">{week.dateRange}</div>
          <div className="week-summary-text">"{week.summary}"</div>
        </div>
      </div>

      {showDetailedCard && (
        <DetailedWeekCard week={week} onLoadMessages={onExpand} />
      )}
    </div>
  );
};

// Detailed Week Card Component
const DetailedWeekCard: React.FC<DetailedWeekCardProps> = ({ week, onLoadMessages }) => {
  return (
    <div className="detailed-week-card">
      <div className="detailed-week-header">
        <div className="detailed-week-title">
          <span className="detailed-week-emoji">{week.emoji}</span>
          <span className="detailed-week-date">{week.dateRange}</span>
        </div>
        <div className="detailed-week-summary">"{week.summary}"</div>
      </div>
      
      <div className="detailed-week-stats">
        <div className="stat-item">
          <MessageCircle className="stat-lucide-icon" size={16} />
          <span className="stat-text">{week.stats.messageCount} messages exchanged</span>
        </div>

        {week.stats.goalsCreated > 0 && (
          <div className="stat-item">
            <Target className="stat-lucide-icon" size={16} />
            <span className="stat-text">{week.stats.goalsCreated} goals created</span>
          </div>
        )}

        {week.stats.workoutsCompleted > 0 && (
          <div className="stat-item">
            <Dumbbell className="stat-lucide-icon" size={16} />
            <span className="stat-text">{week.stats.workoutsCompleted} workouts completed</span>
          </div>
        )}

        {week.stats.breakthroughMoments > 0 && (
          <div className="stat-item">
            <PartyPopper className="stat-lucide-icon" size={16} />
            <span className="stat-text">{week.stats.breakthroughMoments} breakthrough moments</span>
          </div>
        )}
      </div>
      
      {week.keyMoments.length > 0 && (
        <div className="detailed-week-moments">
          <div className="moments-title">Key Moments:</div>
          <ul className="moments-list">
            {week.keyMoments.map((moment, index) => (
              <li key={index} className="moment-item">• {moment}</li>
            ))}
          </ul>
        </div>
      )}
      
      <button 
        className="load-week-button"
        onClick={() => onLoadMessages(week.id)}
      >
        Tap to read full week
      </button>
    </div>
  );
};

// Month Header Component
const MonthHeader: React.FC<MonthHeaderProps> = ({ month, onToggle }) => {
  return (
    <div
      className={`month-header ${month.isExpanded ? 'expanded' : 'collapsed'}`}
      onClick={() => onToggle(month.id)}
    >
      <div className="month-label">
        <CalendarDays className="month-icon" size={14} />
        {month.label}
      </div>
      <div className="month-toggle">
        {month.isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      {month.summary && (
        <div className="month-summary">- "{month.summary}"</div>
      )}
    </div>
  );
};

// Main Chat History Component
export const ChatHistory: React.FC<ChatHistoryProps> = ({
  history,
  onWeekExpand,
  onMonthToggle
}) => {
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  // Track collapsed months — all months are expanded by default
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());

  const handleWeekExpand = (weekId: string) => {
    const newExpanded = new Set(expandedWeeks);

    if (newExpanded.has(weekId)) {
      newExpanded.delete(weekId);
    } else {
      newExpanded.add(weekId);
    }

    setExpandedWeeks(newExpanded);
    onWeekExpand(weekId);
  };

  const handleMonthToggle = (monthId: string) => {
    setCollapsedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthId)) {
        next.delete(monthId);
      } else {
        next.add(monthId);
      }
      return next;
    });
    onMonthToggle(monthId);
  };

  // NOTE: Pre-Phase 6, history.isProcessing rendered a full-screen
  // spinner that blanked the timeline. That's wrong: isProcessing means
  // "the LLM is summarizing one or more weeks", not "there's nothing to
  // show yet". The per-week 'processing' variant handles in-progress
  // weeks inline; the parent's loadingHistory boolean covers initial
  // fetch.

  return (
    <div className="chat-history">
      {/* Current Week Divider */}
      <div className="history-divider current-week" id="history-start">
        <div className="divider-line"></div>
        <div className="divider-text">
          <CalendarDays className="divider-icon" size={18} />
          This Week
        </div>
        <div className="divider-line"></div>
      </div>

      {/* Current Week (if exists and has summary or messages — CH-H4) */}
      {history.currentWeek && (history.currentWeek.summary || (history.currentWeek.stats?.messageCount ?? 0) > 0) && (
        <>
          <WeekCard
            week={history.currentWeek}
            onExpand={handleWeekExpand}
            showDetailedCard={expandedWeeks.has(history.currentWeek.id)}
            variant={computeWeekVariant(history.currentWeek, history.isProcessing)}
          />
          {/* Fixture B: active first week — has messages but no summary
              and no past months. Hint that summaries arrive Sunday. */}
          {!history.currentWeek.summary
            && (history.currentWeek.stats?.messageCount ?? 0) > 0
            && history.months.length === 0 && (
            <div className="first-week-hint">
              Your first weekly summary will appear after this Sunday.
            </div>
          )}
        </>
      )}

      {/* Historical Months and Weeks */}
      {history.months.map((month) => {
        const isExpanded = !collapsedMonths.has(month.id);
        return (
          <div key={month.id} className="month-group">
            <MonthHeader
              month={{ ...month, isExpanded }}
              onToggle={handleMonthToggle}
            />

            {isExpanded && (
              <div className="month-weeks">
                {month.weeks.map((week) => (
                  <WeekCard
                    key={week.id}
                    week={week}
                    onExpand={handleWeekExpand}
                    showDetailedCard={expandedWeeks.has(week.id)}
                    variant={computeWeekVariant(week, history.isProcessing)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty State */}
      {history.months.length === 0 && !history.currentWeek?.summary
        && !((history.currentWeek?.stats?.messageCount ?? 0) > 0) && (
        <div className="history-empty">
          <div className="empty-icon">
            <div className="calendar-icon-cedar">
              <CalendarDays size={32} />
            </div>
          </div>
          <div className="empty-text">Your chat history will appear here as you and Cedar talk.</div>
          <div className="empty-subtext">
            Each Sunday, Cedar reflects on the past week and adds a summary you can scroll back through.
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHistory;
